/**
 * The satchel, as a React island rendered from `@annwn/ui` (RC1-05).
 *
 * The inventory the milestone owner asked to see as a **table**: a framed
 * `Window`, a `Table` of what you carry, and a `Popup` that opens on a row to
 * describe one thing — what it is, what it weighs, how fresh it is, what this
 * character has learned it does, and where this particular one came from. It is
 * the clearest fit in the client for "tables for inventory, windows and popups
 * for descriptions".
 *
 * Like the other islands it holds no game state: `satchelView` (pure, tested)
 * turns the snapshot into rows, and the island only decides which row is open.
 * The numbers it shows — a count and a weight — are the one place the interface
 * is *allowed* numbers (M2-06), because a person knows what is in their own bag.
 */

import { useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { Popup, Table, Window, type TableColumn } from '@annwn/ui';
import type { SatchelSnapshot } from '@annwn/protocol';

import { freshnessLabel, satchelView, type SatchelLine } from './satchel-pane.js';

export interface SatchelOverlayOptions {
  /** The player closed the window — the game folds the satchel shut. */
  onClose(): void;

  /** Drop the exact freshness stack selected in the popup. */
  onDrop(stackKey: string): void;

  /** Take a carried weapon in hand, or put it away if it is already held. */
  onEquip(stackKey: string): void;

  /** Put away a loadout weapon even when it has no satchel row yet. */
  onStow(): void;
}

const columns: readonly TableColumn<SatchelLine>[] = [
  {
    key: 'item',
    header: 'Item',
    cell: (line) => {
      const note = freshnessLabel(line.freshness);
      return (
        <span className={line.spent ? 'satchel-cell satchel-cell--spent' : 'satchel-cell'}>
          {line.label}
          {note ? <span className="satchel-cell__freshness"> — {note}</span> : null}
        </span>
      );
    },
  },
  { key: 'weight', header: 'Weight', cell: (line) => line.weight, align: 'end', width: '6rem' },
];

interface PanelProps extends SatchelOverlayOptions {
  readonly satchel: SatchelSnapshot | null;
  readonly heldWeapon: string | null;
  readonly heldWeaponId: string | null;
  readonly heldWeaponStowable: boolean;
}

function SatchelPanel({
  satchel,
  heldWeapon,
  heldWeaponId,
  heldWeaponStowable,
  onClose,
  onDrop,
  onEquip,
  onStow,
}: PanelProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  if (!satchel) return null;

  const view = satchelView(satchel);
  const selected = view.lines.find((line) => line.key === selectedKey) ?? null;

  return (
    <>
      <Window title="Satchel" onClose={onClose}>
        {heldWeapon ? (
          <p className="satchel-held">
            <span>In hand</span> — {heldWeapon}
            {heldWeaponStowable ? (
              <button type="button" onClick={onStow}>
                Put away
              </button>
            ) : null}
          </p>
        ) : null}
        <Table
          columns={columns}
          rows={view.lines}
          rowKey={(line) => line.key}
          caption={view.weight ? `Carrying ${view.weight}.` : undefined}
          empty={
            heldWeapon
              ? 'Nothing else in the satchel.'
              : (view.empty ?? 'You are carrying nothing.')
          }
          onRowSelect={(line) => setSelectedKey(line.key)}
          selectedKey={selected?.key}
        />
        <p className="satchel-hint">Click a row to look. i or Esc to close.</p>
      </Window>

      <Popup
        open={selected !== null}
        title={selected ? `Examine — ${selected.label}` : ''}
        onClose={() => setSelectedKey(null)}
      >
        {selected ? (
          <div className="satchel-detail">
            <p className="satchel-detail__weight">Weight: {selected.weight}</p>
            {freshnessLabel(selected.freshness) ? (
              <p className="satchel-detail__freshness">{freshnessLabel(selected.freshness)}</p>
            ) : null}
            {selected.note ? <p className="satchel-detail__note">{selected.note}</p> : null}
            {selected.provenance ? (
              <p className="satchel-detail__provenance">{selected.provenance}</p>
            ) : null}
            {selected.equippable ? (
              <button
                type="button"
                onClick={() => {
                  onEquip(selected.key);
                  setSelectedKey(null);
                }}
              >
                {selected.defId === heldWeaponId ? 'Put away' : 'Take in hand'}
              </button>
            ) : null}
            <button
              type="button"
              disabled={selected.defId === heldWeaponId}
              onClick={() => {
                onDrop(selected.key);
                setSelectedKey(null);
              }}
            >
              Drop stack
            </button>
          </div>
        ) : null}
      </Popup>
    </>
  );
}

export interface SatchelOverlayHandle {
  update(
    satchel: SatchelSnapshot | null,
    heldWeapon?: string | null,
    heldWeaponId?: string | null,
    heldWeaponStowable?: boolean,
  ): void;
  setOpen(open: boolean): void;
  destroy(): void;
}

/**
 * Mount the satchel island and return the imperative handle `main.ts` drives.
 *
 * Visibility lives here now rather than on the container's `hidden` attribute:
 * closed, the island renders nothing, so the positioned wrapper is an empty,
 * invisible box. Closing also drops any open description, so reopening the
 * satchel never flashes a stale popup over last time's item.
 */
export function mountSatchelOverlay(
  container: HTMLElement,
  options: SatchelOverlayOptions,
): SatchelOverlayHandle {
  const root: Root = createRoot(container);
  let satchel: SatchelSnapshot | null = null;
  let heldWeapon: string | null = null;
  let heldWeaponId: string | null = null;
  let heldWeaponStowable = false;
  let open = false;

  const render = (): void => {
    root.render(
      open ? (
        <SatchelPanel
          satchel={satchel}
          heldWeapon={heldWeapon}
          heldWeaponId={heldWeaponId}
          heldWeaponStowable={heldWeaponStowable}
          onClose={options.onClose}
          onDrop={options.onDrop}
          onEquip={options.onEquip}
          onStow={options.onStow}
        />
      ) : null,
    );
  };

  render();

  return {
    update(next, nextHeldWeapon = null, nextHeldWeaponId = null, nextHeldWeaponStowable = false) {
      satchel = next;
      heldWeapon = nextHeldWeapon;
      heldWeaponId = nextHeldWeaponId;
      heldWeaponStowable = nextHeldWeaponStowable;
      render();
    },
    setOpen(next) {
      if (open === next) return;
      open = next;
      render();
    },
    destroy() {
      root.unmount();
    },
  };
}
